import { createSelector } from '@reduxjs/toolkit'
import { JSONSchema7 } from 'json-schema'
import isNull from 'lodash/isNull'
import { all, call, put, select, StrictEffect } from 'redux-saga/effects'

import { handleCreateDrawableForGroup } from '../effects/createDrawableGroup'
import { getFloorLevelEnumValues, getSchemaForFloorLevel, matchFloorLevelFromContext } from '../utils/floorLevelUtils'
import { getParentSettings } from './bulkCreateDrawableLocations'
import { convertOpeningsWithGroupIdsToDrawableLocations } from './data-prep/convertOpeningsToDrawableLocations'
import {
    ADD_NEW_DRAWABLE_TO_GROUP,
    filterDrawableGroups,
    resetDrawableGroups,
    SET_ACTIVE_DRAWABLE_GROUP,
    setActiveDrawable,
    setActiveDrawableGroup,
    UPDATE_OPENING_GROUPS_SUCCESS,
} from '../../../actions/drawable'
import { createOpening, CreateOpeningResponse } from '../../../api/projects-api'
import { ActiveDrawable, Coordinate } from '../../../models/activeDrawable'
import { GeneralDrawableSettings } from '../../../models/activeDrawableSettings'
import { ActiveFloor } from '../../../models/activeFloor'
import { Project } from '../../../models/project'
import { Region } from '../../../models/region'
import { DRAWABLE_TYPES, DRAWING_TYPES } from '../../../shared/constants/drawable-types'
import { DRAWING_TYPE_SIZE_VALIDATIONS } from '../../../shared/constants/sizes'
import { RootState } from '../../../stores'
import { calculateAreaFromCoords } from '../../../utils/calculations/areaCalculation/calculateAreaFromCoords'
import { calculateCustomMeasurements } from '../../../utils/calculations/customBusnessFormulas/customFormulas'
import {
    correctHipValleyLFUsingGivenPitch,
    correctMeasurementUsingPitch,
} from '../../../utils/calculations/pitchCorrection/pitchCorrection'
import {
    applyScaleFactorToPathLength,
    convertScaleEnumToString,
    convertScaleFactorLabelEnumToDecimal,
} from '../../../utils/calculations/scaleConversion/scaleConversion'
import {
    buildCoordinatesFromPaperItem,
    getMultipleArcCoordinatesFromPath,
} from '../../../utils/coordinates/buildCoordinates'
import { defineLocationRegion } from '../../../utils/coordinates/defineLocationRegion'
import { getUnitOfMeasureByDrawingType } from '../../../utils/general'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Workspace } from '../../lib/toolBoxes/2D'
import {
    getSelectedMaterial,
    insertDrawableLocation,
    resetNewCreatedDrawables,
    selectedPaperItem,
    selectNewCreatedDrawables,
    setNewCreatedDrawables,
    unsetActiveDrawableId,
} from '../../slices/2D'
import { setInfoModalData } from '../../slices/common'
import { selectActiveChunkScalesAndDPI, selectDrawableActiveFloor } from '../../slices/documents'
import { GENERAL_CACHE } from '../../slices/forms'
import {
    ActiveGeometryGroup,
    createBlankGroup,
    GeometricDrawable,
    Geometry,
    selectActiveGeometryGroup,
    selectActiveGroupToCreate,
    selectGeometries,
} from '../../slices/geometry'
import { selectProject } from '../../slices/project'
import { IMUP2DDrawableCutout, IMUP2DDrawableLocation, MeasurementsToUpdate, OpeningWithGroupId } from '../../types'
import { BEAM_TYPES } from '../../utils/constants'

export const selectScaleFactor = createSelector(
    (state: RootState) => state.IMUP['2D'].scaleFactor,
    (scaleFactor) => scaleFactor
)

/**
 * Determine the pitch corrected area of a given material
 * @param rawArea the raw area
 * @param param1 the settings and the type
 * @returns the pitch corrected area
 */
export const calculateAreaMeasurementsForMaterial = (
    rawArea: number,
    { type, settings }: { type?: DRAWABLE_TYPES; settings?: GeneralDrawableSettings }
): number => {
    const EVName = 'EV'
    const PVName = 'PV'

    if (type && settings) {
        if (settings.pitch) {
            if (type === DRAWABLE_TYPES.EXTERIOR_ROOFING || type === DRAWABLE_TYPES.FRAMING_ROOFING) {
                if (!settings.type) {
                    return rawArea
                }

                return correctMeasurementUsingPitch(
                    parseFloat(String(settings.pitch)),
                    rawArea,
                    settings.type.toUpperCase() === PVName
                )
            }

            // If it is not exterior or framing roofing then use plan view (overhead projection)
            return correctMeasurementUsingPitch(parseFloat(String(settings.pitch)), rawArea, true)
        } else if (type === DRAWABLE_TYPES.ROOFING && settings.name) {
            // Assuming that the group name is EV ### or PV ###
            // Use parse float as the pitch can be a decimal
            const isEV = settings.name.toUpperCase().includes(EVName)
            const isPV = settings.name.toUpperCase().includes(PVName)

            if (!isEV && !isPV) return rawArea
            const pitch = parseFloat(settings.name.split(' ')[1])

            return correctMeasurementUsingPitch(pitch, rawArea, isPV)
        }
    }

    return rawArea
}

/**
 * Determine the linear footage of a given section (modify LF to account for pitch)
 * @param materialGroup the material group the section belongs to
 * @param rawLF the raw linear footage in ft
 * @returns the linear footage of the section
 */
export const calculateLFMeasurementOfMaterial = (
    rawLF: number,
    { type, settings }: { type?: DRAWABLE_TYPES; settings?: GeneralDrawableSettings }
): number => {
    if (type && settings) {
        switch (type) {
            case DRAWABLE_TYPES.VALLEY_PLATE:
            case DRAWABLE_TYPES.ROOF_LINE:
                const pitches: [number, number] = [
                    Number(settings.pitch1 ?? settings.pitch_1),
                    Number(settings.pitch2 ?? settings.pitch_2),
                ]

                return correctHipValleyLFUsingGivenPitch(pitches, rawLF)
            case DRAWABLE_TYPES.GABLE_LENGTH:
            case DRAWABLE_TYPES.BLOCKING:
            case DRAWABLE_TYPES.WILDCARD_LINE:
                return correctMeasurementUsingPitch(parseFloat(String(settings.pitch)), rawLF, true)
            case DRAWABLE_TYPES.BEAM:
                if (settings.beam_type === BEAM_TYPES.HIP_AND_VALLEY_BEAM) {
                    const pitches: [number, number] = [
                        parseFloat(String(settings.pitch_1)),
                        parseFloat(String(settings.pitch_2)),
                    ]

                    return correctHipValleyLFUsingGivenPitch(pitches, rawLF)
                }
                if (settings.beam_type === BEAM_TYPES.FLUSH_BEAM) {
                    return correctMeasurementUsingPitch(parseFloat(String(settings.pitch_1)), rawLF, true)
                }

                return rawLF
            default:
                return rawLF
        }
    }

    return rawLF
}

/**
 * The scale factor to measure a shape with: its region's scale when usable, otherwise the page scale.
 */
export const resolveMeasuringScaleFactor = (
    regions: Region[] | null | undefined,
    regionId: number | null,
    pageScaleFactor: number
): number => {
    const regionScale = convertScaleFactorLabelEnumToDecimal(
        regions?.find((region) => region.id === regionId)?.scale ?? ''
    )

    return regionScale !== 0 ? regionScale : pageScaleFactor
}

export function generateMeasurementsToUpdate({
    path,
    settingsAndType,
    scaleFactor,
    pdfScale,
    xCalibrationFactor,
    yCalibrationFactor,
    coordinates,
    cutouts,
    dpi,
    initialCoordinates,
    initialPathLength,
}: {
    path: paper.Path
    settingsAndType: { type?: DRAWABLE_TYPES; settings?: GeneralDrawableSettings }
    scaleFactor: number
    pdfScale: number
    xCalibrationFactor: number
    yCalibrationFactor: number
    coordinates: Coordinate[]
    cutouts?: IMUP2DDrawableCutout[]
    dpi: number | null
    initialCoordinates?: Coordinate[]
    initialPathLength?: number
}) {
    const measurements: MeasurementsToUpdate = {
        quantity: null,
        linear_total: null,
        area: null,
        count: null,
    }

    if (path.data.shapeType === DRAWING_TYPES.SECTION || path.data.shapeType === DRAWING_TYPES.RADIUS) {
        const linearFootage = calculateLFMeasurementOfMaterial(
            applyScaleFactorToPathLength({
                pxValue: path.length,
                scaleFactor,
                pdfScale,
                xCalibrationFactor,
                yCalibrationFactor,
                coordinates,
                dpi,
            }),
            settingsAndType
        )

        measurements.linear_total = applyScaleFactorToPathLength({
            pxValue: initialPathLength ?? path.length,
            scaleFactor,
            pdfScale,
            xCalibrationFactor,
            yCalibrationFactor,
            coordinates: initialCoordinates ?? coordinates,
            dpi,
        })
        measurements.quantity = linearFootage
    } else if (path.data.shapeType === DRAWING_TYPES.AREA) {
        // For Area materials, quantity is Square & linear_total is Perimeter
        // Computed from the persisted coordinates, not paper.js path.area, so it matches later recomputes
        const convertAreaCoordsToSquareFeet = (areaCoords: Coordinate[]): number =>
            calculateAreaFromCoords({
                coords: areaCoords.flatMap((coord) => coord),
                scaleFactor,
                pdfScale,
                xCalibrationFactor,
                yCalibrationFactor,
                dpi,
            })

        const rawAreaInSquareFeet =
            convertAreaCoordsToSquareFeet(coordinates) -
            (cutouts ?? []).reduce((total, cutout) => total + convertAreaCoordsToSquareFeet(cutout.coordinates), 0)

        measurements.quantity = calculateAreaMeasurementsForMaterial(rawAreaInSquareFeet, settingsAndType)
        measurements.area = rawAreaInSquareFeet
        measurements.linear_total = applyScaleFactorToPathLength({
            pxValue: path.length,
            scaleFactor,
            pdfScale,
            xCalibrationFactor,
            yCalibrationFactor,
            coordinates,
            dpi,
        })
    } else if (path.data.shapeType === DRAWING_TYPES.POINT) {
        // when creating the group there will be 1 point inside it
        measurements.count = 1
        measurements.quantity = calculateCustomMeasurements(measurements, settingsAndType.type).quantity
    }

    // If material is a radius line, pass multiple points to support MaterialCalc model
    const additional_data: Record<string, any> = {}

    if (path.data.shapeType === DRAWING_TYPES.RADIUS) {
        additional_data.arc_points = getMultipleArcCoordinatesFromPath(path)
    }

    return measurements
}

export function* addNewDrawableLocationToStore(drawable: CreateOpeningResponse, drawableGroupId: number) {
    const drawableWithGroupId: OpeningWithGroupId = {
        ...drawable,
        groupId: drawableGroupId,
    }

    const drawableLocations: IMUP2DDrawableLocation[] = yield call(convertOpeningsWithGroupIdsToDrawableLocations, [
        drawableWithGroupId,
    ])

    const drawableLocation = drawableLocations[0]

    // Add new drawable to store
    yield all([
        put({ type: insertDrawableLocation.type, payload: drawableLocation }),
        put({ type: ADD_NEW_DRAWABLE_TO_GROUP, payload: { drawable, drawableGroupId } }),
    ])

    return drawableLocation
}

/**
 * Shared utility function to update drawable group and store with new drawables
 * @param drawableLocations Array of drawable locations to add to store
 * @param createdDrawables Array of created drawables
 * @param activeDrawableGroup Current active drawable group
 * @param newCreatedDrawablesIds Array of existing new drawable IDs
 * @param drawableGroupId ID of the drawable group
 * @param paperItems Optional array of paper items to remove
 */
export function* updateDrawableGroupAndStore({
    drawableLocations,
    createdDrawables,
    activeDrawableGroup,
    newCreatedDrawablesIds,
    drawableGroupId,
    paperItems = [],
}: {
    drawableLocations: IMUP2DDrawableLocation[]
    createdDrawables: CreateOpeningResponse[]
    activeDrawableGroup: ActiveGeometryGroup
    newCreatedDrawablesIds: number[]
    drawableGroupId: number
    paperItems?: paper.Item[]
}) {
    yield all([
        ...drawableLocations.map((location) => put({ type: insertDrawableLocation.type, payload: location })),
        ...createdDrawables.map((drawable) =>
            put({ type: ADD_NEW_DRAWABLE_TO_GROUP, payload: { drawable, drawableGroupId } })
        ),
    ])

    const drawablesWithVisibility: GeometricDrawable[] = createdDrawables.map((drawable) => ({
        ...drawable,
        isVisible: true,
    }))

    const updatedActiveDrawableGroup = {
        ...activeDrawableGroup,
        openings: activeDrawableGroup?.openings
            ? [...activeDrawableGroup.openings, ...drawablesWithVisibility]
            : [...drawablesWithVisibility],
        drawables: activeDrawableGroup?.drawables
            ? [...activeDrawableGroup.drawables, ...drawablesWithVisibility]
            : [...drawablesWithVisibility],
        settings: createdDrawables[0].opening_group.settings,
    }

    yield all([
        put({
            type: UPDATE_OPENING_GROUPS_SUCCESS,
            payload: {
                openingGroups: {
                    newGroup: updatedActiveDrawableGroup,
                    originalGroup: activeDrawableGroup,
                },
                createdDrawableIds: createdDrawables.map((drawable) => drawable.id),
            },
        }),
        ...paperItems.map((item) => call(item.remove.bind(item))),
    ])

    yield put({
        type: SET_ACTIVE_DRAWABLE_GROUP,
        payload: {
            activeDrawableGroup: updatedActiveDrawableGroup,
            activeDrawableId: [...newCreatedDrawablesIds, ...createdDrawables.map((d) => d.id)],
        },
    })

    return updatedActiveDrawableGroup
}

export function* createGroupFromType(
    newDrawableGroupType: DRAWABLE_TYPES,
    project: Project,
    settings: Record<string, any> = {}
) {
    yield handleCreateDrawableForGroup({
        project,
        settings,
        type: newDrawableGroupType,
    })
    const newActiveDrawableGroup: ActiveGeometryGroup = yield select(selectActiveGroupToCreate)

    yield put(createBlankGroup(null))

    return newActiveDrawableGroup
}

export function* handleCreateDrawableLocation2DError(item: paper.Item, isNewActiveDrawableGroup: boolean) {
    // remove item
    yield call(item.remove.bind(item))

    // reset drawable
    yield all([put(unsetActiveDrawableId()), put(resetNewCreatedDrawables()), put(setActiveDrawable(null, 0))])

    // when group is new, reset it
    if (isNewActiveDrawableGroup) {
        yield all([put(setActiveDrawableGroup(null)), put(resetDrawableGroups()), put(filterDrawableGroups())])
    }
}

export function* isValidOpeningSize(measurements: MeasurementsToUpdate, shapeType?: string) {
    if (!shapeType) {
        yield call(console.error, 'Shape type is missing')

        return true
    }

    const validation = DRAWING_TYPE_SIZE_VALIDATIONS[shapeType]

    if (!validation || !measurements[validation.measurement]) {
        yield call(console.error, `Shape ${shapeType} do not contain size validation`)

        return true
    }

    return parseFloat(String(measurements[validation.measurement])) > validation.minValue
}

export function* createDrawableLocation2D({ payload }: { payload: number }): Generator<
    StrictEffect | Coordinate[],
    void,
    PaperManager &
        Workspace &
        (ActiveGeometryGroup | null) &
        (DRAWABLE_TYPES | null) &
        ActiveFloor & {
            dpi: number | null
            xCalibrationFactor: number
            yCalibrationFactor: number
            pdfScale: number
        } & Project &
        (ActiveDrawable | null)
> {
    const manager: PaperManager = yield call(managers.get2DManager)

    if (!manager) return

    const selectedMaterialType: DRAWABLE_TYPES | null = yield select(getSelectedMaterial)
    const newCreatedDrawablesIds = yield select(selectNewCreatedDrawables)
    let activeDrawableGroup: ActiveGeometryGroup | null = yield select(selectActiveGeometryGroup)
    const activeFloor: ActiveFloor = yield select(selectDrawableActiveFloor)
    const project: Project = yield select(selectProject)
    const scaleFactor: number = yield select(selectScaleFactor)
    const {
        dpi,
        xCalibrationFactor,
        yCalibrationFactor,
        pdfScale,
    }: {
        dpi: number | null
        xCalibrationFactor: number
        yCalibrationFactor: number
        pdfScale: number
    } = yield select(selectActiveChunkScalesAndDPI)

    const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)

    const item: paper.Item | null = yield call(workspaceTool.getItemWithPaperId, payload)

    if (!item) return

    if (!activeDrawableGroup && isNull(selectedMaterialType)) {
        yield call(item.remove.bind(item))

        return
    }

    // used to define if we need reset active drawable group or not
    // we should keep active drawable group in case group already existed
    // but we can't create an opening
    const isNewActiveDrawableGroup = activeDrawableGroup === null

    // For rimboard, inherit floor_level, location, secondary_location from the parent material's group
    const isRimboard = selectedMaterialType === DRAWABLE_TYPES.RIMBOARD
    const geometries: Geometry[] | null = isRimboard ? yield select(selectGeometries) : null
    const parentSettings = isRimboard && geometries ? getParentSettings(item, geometries, workspaceTool) : null

    // Determine region early so floor_level can be auto-detected before group creation
    const path = item as paper.Path
    const regions = activeFloor.document_chunk.regions
    const region_id = defineLocationRegion(path, regions, workspaceTool)

    // Get schema to auto-detect floor_level from region or page name
    const buildingId = activeFloor?.building_id?.toString() ?? GENERAL_CACHE
    const schemaForFloorLevel = (yield call(
        getSchemaForFloorLevel,
        selectedMaterialType,
        buildingId
    )) as JSONSchema7 | null

    const floorLevelEnumValues = getFloorLevelEnumValues(schemaForFloorLevel)
    const autoFloorLevel = floorLevelEnumValues
        ? matchFloorLevelFromContext(floorLevelEnumValues, region_id, regions, activeFloor.page_name)
        : null

    let drawableGroupId = activeDrawableGroup ? activeDrawableGroup.id : null

    if (drawableGroupId === null && !isNull(selectedMaterialType)) {
        const parentSettingsWithoutSourceMeasurementId = parentSettings
            ? (({ source_measurement_id: _ignored, ...rest }) => rest)(parentSettings)
            : {}

        const newlyCreatedActiveDrawableGroup: ActiveDrawable | null = yield call(
            createGroupFromType,
            selectedMaterialType,
            project,
            {
                ...item.data.aiSuggestion?.settings,
                shape_type: item.data.shapeType,
                ...(autoFloorLevel && { floor_level: autoFloorLevel }),
                ...parentSettingsWithoutSourceMeasurementId,
            }
        )

        drawableGroupId = newlyCreatedActiveDrawableGroup ? newlyCreatedActiveDrawableGroup.id : null
        activeDrawableGroup = newlyCreatedActiveDrawableGroup ? (newlyCreatedActiveDrawableGroup as any) : null
    }

    if (isNull(drawableGroupId)) {
        // There was an issue creating the group so delete
        // element and bail
        yield call(item.remove.bind(item))

        return
    }

    const activeFloorHash = activeFloor.hash
    const projectId = project.id
    const documentChunkId = activeFloor.document_chunk.id

    const coordinates: Coordinate[] = yield call(buildCoordinatesFromPaperItem, item, path)

    const measurements = generateMeasurementsToUpdate({
        path,
        settingsAndType: { type: activeDrawableGroup?.type, settings: activeDrawableGroup?.settings },
        scaleFactor: resolveMeasuringScaleFactor(regions, region_id, scaleFactor),
        xCalibrationFactor,
        yCalibrationFactor,
        dpi,
        pdfScale,
        coordinates,
    })

    const isValidShapeSize: boolean = yield call(isValidOpeningSize, measurements, item?.data?.shapeType)

    if (!isValidShapeSize) {
        yield put(
            setInfoModalData({
                visible: true,
                title: 'Error',
                body: 'Material is too small and could not be created that size.',
            })
        )

        yield call(handleCreateDrawableLocation2DError, item, isNewActiveDrawableGroup)

        return
    }

    // If material is a radius line, pass multiple points to support MaterialCalc model
    const additional_data: Record<string, any> = {}

    if (path.data.shapeType === DRAWING_TYPES.RADIUS) {
        additional_data.arc_points = getMultipleArcCoordinatesFromPath(path)
    }

    if (item.data.parentSystem) {
        additional_data.parent_system = item.data.parentSystem
    }

    if (region_id) {
        const regionPath = workspaceTool.getRegionItemByRegionId(region_id)

        additional_data.scale_factor = convertScaleEnumToString(regionPath.data.scale)
    } else {
        additional_data.scale_factor = convertScaleEnumToString(activeFloor.scale_factor)
    }

    try {
        const drawable: CreateOpeningResponse = yield call(
            createOpening,
            projectId,
            activeFloorHash,
            drawableGroupId,
            coordinates,
            documentChunkId,
            measurements,
            {
                unit_of_measure: getUnitOfMeasureByDrawingType(path.data.shapeType),
                shape_type: path.data.shapeType,
                ...parentSettings,
            },
            region_id,
            additional_data,
            path.data.aiSuggestion?.id
        )

        // set the drawable ids as latest added
        yield put(setNewCreatedDrawables(drawable.id))

        const drawableLocation: IMUP2DDrawableLocation = yield call(
            addNewDrawableLocationToStore,
            drawable,
            drawableGroupId
        )

        // Use shared utility function to update drawable group and store
        const newActiveDrawableGroup: ActiveGeometryGroup = yield call(updateDrawableGroupAndStore, {
            drawableLocations: [drawableLocation],
            createdDrawables: [drawable],
            activeDrawableGroup: activeDrawableGroup!,
            newCreatedDrawablesIds,
            drawableGroupId,
            paperItems: [item],
        })

        if (item.id && drawable.id) {
            yield put(
                selectedPaperItem({
                    selectedItem: item.id,
                    activeDrawableId: drawable.id,
                    activeOpeningLocationId: drawableLocation.opening_location_id ?? null,
                    openingGroupId: newActiveDrawableGroup.id ?? null,
                })
            )
        }
    } catch (error) {
        yield call(console.error, error)

        yield call(handleCreateDrawableLocation2DError, item, isNewActiveDrawableGroup)
    }
}
